import api from '@/core/api/client';

/** Purga operacional e destroy de shell apagam muitos blobs/linhas; o axios padrão (30s) corta antes de concluir. */
const LONG_OPERATION_TIMEOUT_MS = 600_000; // 10 min

export interface Company {
  id: string;
  name: string;
  fantasyName?: string | null;
  slug: string;
  document?: string | null;
  documentType?: 'CNPJ' | 'CPF' | 'EIN' | 'NIF' | 'OTHER' | 'NONE' | null;
  customerType?: 'INDIVIDUAL' | 'COMPANY' | null;
  country?: string | null;
  email?: string | null;
  phone?: string | null;
  mobilePhone?: string | null;
  addressLine1?: string | null;
  addressLine2?: string | null;
  postalCode?: string | null;
  city?: string | null;
  stateOrProvince?: string | null;
  formattedAddress?: string | null;
  placeId?: string | null;
  municipalInscription?: string | null;
  stateInscription?: string | null;
  gatewayProvider?: string | null;
  gatewayCustomerId?: string | null;
  subscriptionStatus?: string | null;
  status?: string | null; // ACTIVE | PURGED
  defaultLanguage?: string | null;
  timezone?: string | null;
  createdAt: string;
  updatedAt: string;
  deletedAt?: string;
  hasActiveSubscription?: boolean;
  subscription?: unknown;
}

export interface PaginationParams {
  status?: 'ACTIVE' | 'PURGED';
  subscriptionStatus?: 'ACTIVE' | 'TRIALING' | 'PAST_DUE' | 'CANCELED' | 'PENDING_PAYMENT' | 'NONE';
  page?: number;
  limit?: number;
  search?: string;
  includeDeleted?: boolean;
  /** Só aplica com includeDeleted: lista shells PURGED (arquivo financeiro). */
  includePurged?: boolean;
}

export interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

/** CNPJ lookup response (backend proxy - Brasil API) */
export interface CompanyFormFromCnpj {
  name: string;
  fantasyName: string;
  document: string;
  email: string;
  phone: string;
  addressLine1: string;
  addressLine2: string;
  postalCode: string;
  city: string;
  stateOrProvince: string;
  formattedAddress: string;
  placeId?: string;
}

export interface CompanyBillingData {
  document?: string | null;
  documentType?: 'CNPJ' | 'CPF' | 'EIN' | 'NIF' | 'OTHER' | 'NONE' | null;
  customerType?: 'INDIVIDUAL' | 'COMPANY' | null;
  country?: string | null;
  email?: string | null;
  phone?: string | null;
  mobilePhone?: string | null;
  addressLine1?: string | null;
  addressLine2?: string | null;
  postalCode?: string | null;
  city?: string | null;
  stateOrProvince?: string | null;
  formattedAddress?: string | null;
  placeId?: string | null;
  municipalInscription?: string | null;
  stateInscription?: string | null;
}

export type PaymentMethodAsaas = 'PIX' | 'BOLETO' | 'CREDIT_CARD' | 'MANUAL';

export interface CreateCompanySubscriptionItem {
  productId: string;
  quantity: number;
}

export interface CreateCompanyData extends CompanyBillingData {
  name: string;
  fantasyName?: string;
  slug?: string;
  planId: string;
  seatCount: number;
  additionalConnectionCount?: number;
  licensingCurrency: string;
  trialDays?: number;
  /** Subscription add-ons (products) */
  items?: CreateCompanySubscriptionItem[];
  /** Asaas payment method: PIX, Boleto or Card */
  paymentMethod?: PaymentMethodAsaas;
  /** OWNER da empresa — backend cria user + member OWNER + dept default. */
  ownerEmail: string;
  ownerName: string;
  /** Opcional — backend gera senha temp se vazio. */
  ownerPassword?: string;
}

export interface CreateCompanyResponse {
  company: Company;
  /** Credenciais do OWNER recém-criadas. Senha só é retornada UMA vez aqui. */
  owner?: { email: string; temporaryPassword: string };
  paymentUrl?: string;
  invoiceUrl?: string;
}

export interface UpdateCompanyData extends CompanyBillingData {
  name?: string;
  fantasyName?: string;
  slug?: string;
  defaultLanguage?: string;
  timezone?: string;
}

export interface CompanyStatsPayload {
  activeUsers: number;
  whatsappConnections: number;
  limits: Record<string, number>;
  hasActiveSubscription: boolean;
  subscription: unknown;
  /** Minutos de transcrição consumidos no mês UTC corrente (feature_usage). */
  transcriptionMinutesUsed?: number;
  /** Cota efetiva (plano + itens + bônus staff) em minutos/mês. */
  transcriptionMinutesLimit?: number;
}

export interface CompanyStatsResponse {
  company: Company;
  stats: CompanyStatsPayload;
}

export const companiesService = {
  async list(params: PaginationParams = {}): Promise<PaginatedResponse<Company>> {
    const response = await api.get('/companies', { params });
    if (response.data.success) {
      return {
        data: response.data.data,
        pagination: response.data.pagination,
      };
    }
    throw new Error(response.data.message || 'Error listing companies');
  },

  async get(id: string): Promise<Company> {
    const response = await api.get(`/companies/${id}`);
    if (response.data.success) {
      return response.data.data;
    }
    throw new Error(response.data.message || 'Error fetching company');
  },

  async getStats(id: string): Promise<CompanyStatsResponse> {
    const response = await api.get(`/companies/${id}/stats`);
    if (response.data.success) {
      return response.data.data;
    }
    throw new Error(response.data.message || 'Erro ao carregar estatísticas da empresa');
  },

  async create(data: CreateCompanyData): Promise<CreateCompanyResponse> {
    const response = await api.post('/companies', data);
    if (response.data.success) {
      return {
        company: response.data.data,
        owner: response.data.owner,
        paymentUrl: response.data.paymentUrl,
        invoiceUrl: response.data.invoiceUrl,
      };
    }
    throw new Error(response.data.message || 'Error creating company');
  },

  async update(id: string, data: UpdateCompanyData): Promise<Company> {
    const response = await api.put(`/companies/${id}`, data);
    if (response.data.success) {
      return response.data.data;
    }
    throw new Error(response.data.message || 'Error updating company');
  },

  async delete(id: string): Promise<void> {
    const response = await api.delete(`/companies/${id}`);
    if (!response.data.success) {
      throw new Error(response.data.message || 'Error deleting company');
    }
  },

  async restore(id: string): Promise<void> {
    const response = await api.post(`/companies/${id}/restore`);
    if (!response.data.success) {
      throw new Error(response.data.message || 'Error restoring company');
    }
  },

  async hardDelete(id: string): Promise<void> {
    const response = await api.delete(`/companies/${id}/permanent`, {
      timeout: LONG_OPERATION_TIMEOUT_MS,
    });
    if (!response.data.success) {
      throw new Error(response.data.message || 'Error permanently deleting company');
    }
  },

  /** Só para empresa PURGED: remove assinaturas/itens e referências de gateway no Orya. */
  async eraseFinancialHistory(id: string, confirmSlug: string): Promise<void> {
    const response = await api.post(`/companies/${id}/erase-financial-history`, { confirmSlug });
    if (!response.data.success) {
      throw new Error(response.data.message || 'Erro ao apagar histórico financeiro');
    }
  },

  async bulkEraseFinancialHistory(
    companyIds: string[],
    confirm: 'APAGAR HISTORICO'
  ): Promise<Array<{ id: string; ok: boolean; error?: string }>> {
    const response = await api.post(
      '/companies/bulk-erase-financial-history',
      {
        companyIds,
        confirm,
      },
      { timeout: LONG_OPERATION_TIMEOUT_MS }
    );
    if (!response.data.success) {
      throw new Error(response.data.message || 'Erro no lote');
    }
    return response.data.data.results;
  },

  /** Remove a linha `companies` e cascatas (só PURGED). Frase + slug obrigatórios. */
  async destroyPurgedShell(
    id: string,
    confirmSlug: string,
    confirmPhrase: 'SEM RASTRO NO ORYA'
  ): Promise<void> {
    const response = await api.post(
      `/companies/${id}/destroy-purged-shell`,
      {
        confirmSlug,
        confirmPhrase,
      },
      { timeout: LONG_OPERATION_TIMEOUT_MS }
    );
    if (!response.data.success) {
      throw new Error(response.data.message || 'Erro ao excluir shell');
    }
  },

  async bulkDestroyPurgedShell(
    companyIds: string[],
    confirm: 'EXCLUIR SEM RASTRO'
  ): Promise<Array<{ id: string; ok: boolean; error?: string }>> {
    const response = await api.post(
      '/companies/bulk-destroy-purged-shell',
      {
        companyIds,
        confirm,
      },
      { timeout: LONG_OPERATION_TIMEOUT_MS }
    );
    if (!response.data.success) {
      throw new Error(response.data.message || 'Erro no lote');
    }
    return response.data.data.results;
  },

  async cnpjLookup(document: string): Promise<CompanyFormFromCnpj> {
    const response = await api.get('/companies/cnpj-lookup', {
      params: { document },
    });
    if (response.data.success) {
      return response.data.data;
    }
    throw new Error(response.data.message || 'Error looking up CNPJ');
  },

  async checkDocument(document: string): Promise<{ exists: boolean; message?: string }> {
    const response = await api.get('/companies/check-document', {
      params: { document },
    });
    if (response.data.success) {
      return response.data.data;
    }
    return { exists: false };
  },

  async createAsaasCustomer(id: string): Promise<{ gatewayCustomerId: string }> {
    const response = await api.post(`/companies/${id}/create-asaas-customer`);
    if (!response.data.success) {
      throw new Error(response.data.message || 'Error creating Asaas customer');
    }
    return response.data.data;
  },

  async addAliciaTokenBonus(
    companyId: string,
    addTokens: number
  ): Promise<{ companyId: string; bonusTokens: number }> {
    const response = await api.post(`/companies/${companyId}/alicia-token-bonus`, { addTokens });
    if (response.data.success) {
      return response.data.data;
    }
    throw new Error(response.data.message || 'Erro ao adicionar bônus Alici.IA');
  },

  async addTranscriptionMinutesBonus(
    companyId: string,
    addMinutes: number
  ): Promise<{ companyId: string; bonusMinutes: number }> {
    const response = await api.post(`/companies/${companyId}/transcription-minutes-bonus`, { addMinutes });
    if (response.data.success) {
      return response.data.data;
    }
    throw new Error(response.data.message || 'Erro ao adicionar bônus de transcrição');
  },

  async generateAccessToken(
    companyId: string
  ): Promise<{ accessToken: string; companyName: string; appRole: string; expiresIn: string }> {
    const response = await api.post(`/companies/${companyId}/access-token`);
    if (response.data.success) {
      return response.data;
    }
    throw new Error(response.data.message || 'Erro ao gerar token de acesso');
  },

  async listIntegrationEntitlements(companyId: string): Promise<CompanyIntegrationEntitlement[]> {
    const response = await api.get(`/companies/${companyId}/integration-entitlements`);
    if (response.data.success) return response.data.data;
    throw new Error(response.data.message || 'Erro ao consultar as integrações da empresa');
  },

  async updateIntegrationEntitlement(
    companyId: string,
    integrationId: string,
    input: { enabled: boolean; reason: string },
  ): Promise<{ enabled: boolean; previousEnabled: boolean; gatewaysAffected: number }> {
    const response = await api.patch(`/companies/${companyId}/integration-entitlements/${integrationId}`, input);
    if (response.data.success) return response.data;
    throw new Error(response.data.message || 'Erro ao alterar a integração');
  },

  async listMembers(companyId: string): Promise<CompanyMember[]> {
    const response = await api.get(`/companies/${companyId}/members`);
    if (response.data.success) {
      return response.data.data;
    }
    throw new Error(response.data.message || 'Erro ao listar usuários da empresa');
  },

  async verifyMemberEmail(companyId: string, userId: string): Promise<{ verifiedAt: string }> {
    const response = await api.post(`/companies/${companyId}/members/${userId}/verify-email`);
    if (response.data.success) {
      return response.data.data;
    }
    throw new Error(response.data.message || 'Erro ao validar email');
  },

  async anonymizeMember(
    companyId: string,
    userId: string
  ): Promise<{ memberDeleted: boolean; userAnonymized: boolean; remainingMemberships: number }> {
    const response = await api.post(`/companies/${companyId}/members/${userId}/anonymize`);
    if (response.data.success) {
      return response.data.data;
    }
    throw new Error(response.data.message || 'Erro ao excluir permanentemente usuário');
  },
};

export interface CompanyIntegrationEntitlement {
  integrationId: string;
  displayName: string;
  category: string;
  type: string;
  providerStatus: string;
  defaultEntitled: boolean;
  /** null = sem registro (vale o padrão do catálogo). */
  entitlement: {
    id: string;
    enabled: boolean;
    source: string;
    enabledAt: string;
    updatedAt: string;
    notes: string | null;
  } | null;
  /** Estado efetivo: o que o cliente consegue usar de fato. */
  effectiveEnabled: boolean;
  /** O plano libera o módulo (null = não depende de capacidade do plano). */
  planAllows: boolean | null;
  /** Plano libera (módulo visível), mas o entitlement bloqueia o uso. */
  divergence: 'PLAN_ALLOWS_ENTITLEMENT_DISABLED' | null;
  gateways: { total: number; enabled: number };
  lastChange: { at: string; byType: string; byEmail: string | null; reason: string | null } | null;
}

export interface CompanyMember {
  memberId: string;
  userId: string;
  companyId: string;
  email: string;
  name: string | null;
  role: string;
  isActive: boolean;
  emailVerifiedAt: string | null;
  lastLoginAt: string | null;
  createdAt: string;
  updatedAt: string;
}
